<template>
  <Transition name="popup-transition">
    <div
      v-if="isVisible"
      class="popup"
      :class="{
        'popup--compact': compact,
        'popup--fullscreen': fullscreen,
        'popup--popout': popout,
        'popup--menu': menu,
        'popup--anchored': !!anchor,
      }"
      @click="$event.stopPropagation()"
    >
      <div class="popup__background" @click="hide()"></div>

      <div
        ref="popupElement"
        class="popup__window"
        :style="[currentPositionCssVariable, anchorPosition]"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
        @touchcancel="onTouchEnd"
      >
        <div v-if="!menu || title || $slots.headerAction" class="popup__header">
          <h2 v-if="title" class="popup__title" v-html="title"></h2>
          <slot name="headerAction"></slot>
          <button v-if="!menu" class="popup__close" @click="hide()">
            <span class="popup__close-icon iconfont iclose-thin" />
          </button>
        </div>

        <div class="popup__inner">
          <slot></slot>

          <div v-if="$slots.buttons" class="popup__buttons">
            <slot name="buttons"></slot>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { closestMatching } from '@/helpers/dom'
import { platform } from '@/services/platform'
import { computed, nextTick, onUnmounted, ref, useTemplateRef } from 'vue'

const props = defineProps<{
  title?: string
  compact?: boolean
  fullscreen?: boolean
  popout?: boolean
  /**
   * Narrow style popup without a close button. The header is a small title and
   * the optional `headerAction` slot, and is left out when there are neither.
   * We use this for the destination menu, where the headerAction is
   * "New List", and the doc "more" menu.
   */
  menu?: boolean
  /**
   * On desktop, position the popup next to this element.
   */
  anchor?: HTMLElement | null
}>()

const emit = defineEmits<{
  (e: 'hide', trackEvent: boolean): void
}>()

const popupElement = useTemplateRef('popupElement')
const isVisible = ref(false)

// Selector for any focusable element. Use this when moving focus to the menu.
const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'

// In activeElementBeforeShow, store the element that had focus before opening
// the menu, so we can move focus back to that once the menu is closed.
let activeElementBeforeShow: HTMLElement | null = null

function hide(trackEvent: boolean = true): void {
  const wasVisible = isVisible.value
  isVisible.value = false
  document.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', updateAnchorPosition)
  if (props.menu && wasVisible) {
    activeElementBeforeShow?.focus({ preventScroll: true })
    activeElementBeforeShow = null
  }
  emit('hide', trackEvent)
}

function show(): void {
  isVisible.value = true
  currentY.value = 0
  document.addEventListener('keydown', onKeydown)
  if (props.menu) {
    // In menu mode, move focus to itself when it opens, so that keyboard users
    // don't have to tab through the page behind it to reach it.
    activeElementBeforeShow =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
    // Wait for the menu to render before focusing its first item.
    nextTick(() => {
      popupElement.value
        ?.querySelector<HTMLElement>(FOCUSABLE)
        ?.focus({ preventScroll: true })
    })
  }
  if (props.anchor) {
    // Follow the anchor if the window is resized (e.g. the page reflows).
    // There's no scroll listener: the backdrop blocks wheel scrolling, and
    // keyboard scrolling has no effect because focus isn't inside the page's
    // scroller (`#app`). The one exception is tabbing to an off-screen
    // element behind the popup, which we don't handle.
    window.addEventListener('resize', updateAnchorPosition)
    // Wait for the popup to render so that we know its size to position it.
    nextTick(updateAnchorPosition)
  }
}

// The space between the anchor element and the popup.
const ANCHOR_GAP = 8
// The minimum distance between the popup and the viewport boundary.
const VIEWPORT_MARGIN = 16
const anchorPosition = ref<Record<string, string>>({})

/**
 * On desktop, position the popup next to the anchor element.
 */
function updateAnchorPosition(): void {
  // The popup isn't rendered if it was hidden before the `nextTick()` in
  // `show()` ran.
  const popup = popupElement.value
  if (!props.anchor || !popup) {
    return
  }

  const anchorRect = props.anchor.getBoundingClientRect()

  // Vertically clamp to the viewport.
  const topIfBelow = Math.max(VIEWPORT_MARGIN, anchorRect.bottom + ANCHOR_GAP)
  const bottomIfAbove = Math.max(
    VIEWPORT_MARGIN,
    window.innerHeight - anchorRect.top + ANCHOR_GAP,
  )
  const spaceBelow = window.innerHeight - topIfBelow - VIEWPORT_MARGIN
  const spaceAbove = window.innerHeight - bottomIfAbove - VIEWPORT_MARGIN

  // Calculate the popup's full height, including any content cut off by max-height.
  // - scrollHeight: content + padding + content hidden by max-height
  // - offsetHeight: content + padding + border
  // - clientHeight: content + padding
  const height = popup.scrollHeight + popup.offsetHeight - popup.clientHeight

  // Position the popup
  // - below the anchor element if it fits (spaceBelow >= height)
  // - otherwise above if it fits there
  // - otherwise whichever side has more room
  // The second condition in the OR checks if the popup doesn't fit above
  // (spaceAbove < height) and if there is less space above than below
  // (spaceBelow >= spaceAbove).
  const placePopupBelowAnchor =
    spaceBelow >= height || (spaceAbove < height && spaceBelow >= spaceAbove)

  // `right` is the distance from the right edge of the window to the right
  // edge of the popup.
  // Right-align to the anchor element when possible, while keeping the popup
  // inside the viewport.
  const right = Math.min(
    Math.max(
      // The "normal" case: just the right edge of the anchor element.
      window.innerWidth - anchorRect.right,
      // The right edge of the anchor element is too close to the viewport
      // right edge. Use the minimum margin from the viewport.
      VIEWPORT_MARGIN,
    ),
    // The left edge of the popup is too close to the viewport left edge.
    // Subtract (popup width + minimum margin) from the window's width to get
    // the leftmost position we allow for the right edge of the popup.
    window.innerWidth - popup.offsetWidth - VIEWPORT_MARGIN,
  )

  anchorPosition.value = {
    '--popup-anchor-top': placePopupBelowAnchor ? `${topIfBelow}px` : 'auto',
    '--popup-anchor-bottom': placePopupBelowAnchor
      ? 'auto'
      : `${bottomIfAbove}px`,
    '--popup-anchor-right': `${right}px`,
    '--popup-anchor-max-height': `${placePopupBelowAnchor ? spaceBelow : spaceAbove}px`,
  }
}

/**
 * Hide the popup when the user presses "Esc".
 */
function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    hide()
  }
}

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', updateAnchorPosition)
})

// The logic below handles the logic for closing the popup
// by swiping down. It's used only at the mobile breakpoint,
// where the popup is styled as a bottom sheet element.
const sheetHeight = ref(0)
const startTime = ref(0)
const startY = ref(0)
const currentY = ref(0)
const currentPositionCssVariable = computed(() => {
  return `--sheet-current-position: ${currentY.value}px`
})

function onTouchStart(event: TouchEvent): void {
  if (props.fullscreen || !platform.isMediumWidth()) {
    // Disable drag-to-close if the popup is set to fullscreen,
    // or if it's above the medium breakpoint (tablet and desktop).
    return
  }

  if (isScrolling(event.target as HTMLElement)) {
    // Set `startTime` to 0 to indicate that we shouldn't
    // run the remaining event handlers (otherwise scrolling
    // content would drag the whole sheet with it).
    startTime.value = 0
    return
  }

  // This will be used to calculate the swipe velocity
  startTime.value = Date.now()
  // This will be used to calculate the swipe distance.
  startY.value = event.touches[0].clientY
  // Update sheetHeight if the element's height has changed.
  sheetHeight.value = popupElement.value?.offsetHeight || sheetHeight.value
  // Mark the element as being dragged. This is used in the CSS
  // to remove the `transition` property (the element's position
  // should update immediately, otherwise it feels unresponsive).
  if (popupElement.value) {
    popupElement.value.dataset.isDragged = 'true'
  }
}

function onTouchMove(event: TouchEvent): void {
  if (!startTime.value) {
    // No `startTime` indicates that we shouldn't run update the sheet's positon
    // because the user is currently scrolling the content inside.
    return
  }

  // The current position of the finger
  const touchY = event.touches[0].clientY
  const distanceMoved = touchY - startY.value

  if (distanceMoved <= 0) {
    // Don't let the component move beyond its initial position
    currentY.value = 0
  } else {
    currentY.value = distanceMoved
  }
}

function onTouchEnd(): void {
  // End the `isDragged` state.
  delete popupElement.value?.dataset.isDragged

  if (!startTime.value) {
    // Reset the sheet's position, just in case.
    currentY.value = 0
    return
  }

  // The time spent dragging
  const timeSpan = Date.now() - startTime.value
  // The velocity the drag action
  const velocity = currentY.value / timeSpan

  // Threshold velocity in pixels per millisecond.
  // There's not much science to this number, it's just
  // a value that feels good.
  const velocityThreshold = 0.2
  const halfComponentHeight = sheetHeight.value / 2

  if (
    (velocity > velocityThreshold && currentY.value > 50) ||
    currentY.value > halfComponentHeight
  ) {
    // If the user swiped quick enough or if they
    // swiped to below half the height of the element,
    // close it.
    hide()
  } else {
    // Spring it back to its initial position
    currentY.value = 0
  }

  // Reset the startTime.
  startTime.value = 0
}

/**
 * Check if the touch event target is being scrolled.
 */
function isScrolling(targetElement: HTMLElement): boolean | null {
  const scrollingElement = closestMatching(
    targetElement,
    (element) => element.scrollHeight > element.clientHeight,
    popupElement.value,
    true,
  )
  return scrollingElement && scrollingElement.scrollTop > 0
}

defineExpose({
  show,
  hide,
  isVisible,
})
</script>

<style lang="scss">
html:has(.popup) {
  // Prevent overscrolling (and Android's pull to refresh)
  // while the popup is open.
  overscroll-behavior: none;
}
</style>

<style lang="scss" scoped>
.popup {
  --popup-width: 680px;
  --sheet-margin-top: 32px;
  @supports (padding: env(safe-area-inset-top)) {
    --sheet-margin-top: max(32px, calc(env(safe-area-inset-top) + 16px));
  }
  position: fixed;
  inset: 0;
  z-index: 999;
  display: grid;
  place-items: center;
  overflow: hidden;

  &__background {
    position: absolute;
    inset: 0;
    background: rgba($c-black-2, 0.3);
  }

  &__window {
    position: relative;
    width: 100%;
    max-width: var(--popup-width);
    max-height: calc(100% - var(--sheet-margin-top));
    padding: 60px;
    overflow: auto;
    border-radius: 16px;
    background: $c-white;
    box-shadow: 0px 24px 54px rgba(20, 28, 49, 0.14);
    // This dataset is set in `onTouchStart` and `onTouchEnd`.
    // We don't set `transition` when the sheet is being dragged
    // because it would cause it to feel unresponsive and janky.
    &:not([data-is-dragged]) {
      transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    }

    @media (max-width: $w-medium-s) {
      position: absolute;
      bottom: 0;
      left: 0;
      max-width: none;
      padding-inline: 24px;
      padding-block: 40px 28px;
      border-radius: 14px 14px 0 0;
      transform: translateY(var(--sheet-current-position));

      // The "handle" line on top of the sheet.
      &:before {
        content: '';
        position: absolute;
        top: 16px;
        left: calc(50% - 40px);
        width: 80px;
        height: 4px;
        border-radius: 4px;
        background: $c-black-13;
      }
    }
  }

  &__close {
    position: absolute;
    top: 20px;
    right: 20px;
    display: block;
    height: fit-content;
    color: $c-black-6;
    @include hover {
      color: $c-black-2;
    }

    @media (max-width: $w-medium-s) {
      display: none;
    }
  }

  &__close-icon {
    display: block;
    font-size: 40px;
    width: 40px;
    height: 40px;
  }

  &__title {
    font-size: 32px;
    line-height: 40px;
    letter-spacing: -0.02em;
    font-weight: 500;
    text-align: center;
    color: $c-black-2;
    margin-bottom: 12px;

    @media (max-width: $w-medium-s) {
      font-size: 28px;
      line-height: 32px;
      font-weight: 500;
      margin-bottom: 12px;
    }
  }

  &__inner {
    :deep(> p:not([class])) {
      margin-bottom: 0;
      font-size: 16px;
      line-height: 22px;
      letter-spacing: -0.02em;
      text-align: center;
      color: $c-black-15;
    }
  }

  &__buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 24px;
    margin-top: 48px;

    @media (max-width: $w-medium-s) {
      flex-direction: column-reverse;
      gap: 8px;
      margin-top: 24px;

      > * {
        width: 100%;
      }
    }
  }
}

.popup--compact {
  --popup-width: 450px;

  .popup {
    &__window {
      padding: 16px 36px;
      border-radius: 12px;

      @media (max-width: $w-medium-s) {
        padding-inline: 24px;
        padding-block: 40px 28px;
        border-radius: 14px 14px 0 0;
      }
    }

    &__header {
      display: flex;
      flex-wrap: nowrap;
    }

    &__close {
      position: static;
    }

    &__close-icon {
      font-size: 30px;
      width: 30px;
      height: 30px;
      flex-shrink: 0;
    }

    &__title {
      width: 100%;
      font-size: 24px;
      line-height: 32px;
      margin-bottom: 10px;
      text-align: start;

      @media (max-width: $w-medium-s) {
        text-align: center;
      }
    }
  }
}

// Menu style, used by the Save destination menu (experiment 20321B).
.popup--menu {
  --popup-width: 320px;

  .popup {
    &__window {
      @media (min-width: $w-medium-b) {
        padding: 20px 24px;
        border: 1px solid $c-black-13;
        border-radius: 12px;
      }

      .night-mode & {
        background: $c-dark-3;
      }
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 16px;
    }

    &__title {
      // Shift the text down 2px so that it looks vertically centered next to
      // a small button in the `headerAction` slot. This is necessary because
      // the font letters have more space below than above.
      padding-top: 4px;
      margin-bottom: 0;
      font-size: 18px;
      line-height: 24px;
      letter-spacing: normal;
      text-align: start;
    }
  }
}

.popup-transition {
  @include transition-default();

  &-enter-from,
  &-leave-to {
    opacity: 0;

    @media (max-width: $w-medium-s) {
      .popup__window {
        transform: translateY(100%);
      }
    }
  }

  &-enter-to,
  &-leave-from {
    opacity: 1;

    @media (max-width: $w-medium-s) {
      .popup__window {
        transform: translateY(var(--sheet-current-position));
      }
    }
  }
}

.popup--fullscreen {
  .popup {
    &__window {
      @media (max-width: $w-medium-s) {
        padding-top: 20px;
        height: 100%;
        &:before {
          display: none;
        }
      }
    }

    &__close {
      @media (max-width: $w-medium-s) {
        display: block;

        // Render it a bit higher when it is the
        // first child (ie. there's no title).
        &:where(:not(:first-child)) {
          top: -20px;
        }
      }
    }
  }
}

.popup--popout {
  &.popup-transition {
    @include transition-default();

    &-enter-from,
    &-leave-to {
      @media (min-width: $w-medium-b) {
        .popup__window {
          transform: scale(0.7);
        }
      }
    }

    &-enter-to,
    &-leave-from {
      @media (min-width: $w-medium-b) {
        .popup__window {
          transform: scale(1);
        }
      }
    }
  }
}

.night-mode .popup {
  &__background {
    background: rgba($c-black-7, 0.67);
  }

  &__window {
    background: $c-black-7;
    border: 1px solid $c-dark-5;

    &:before {
      background: $c-border-6;
    }
  }

  &__close {
    color: $c-border-6;
    @include hover {
      color: $c-black-11;
    }
  }

  &__title {
    color: $c-black-11;
  }

  &__inner {
    :deep(> p:not([class])) {
      color: $c-black-11;
    }
  }
}

// Placed after the night mode styles so the transparent backdrop wins.
.popup--anchored {
  @media (min-width: $w-medium-b) {
    .popup {
      &__background {
        background: transparent;
      }

      &__window {
        position: absolute;
        top: var(--popup-anchor-top);
        right: var(--popup-anchor-right);
        bottom: var(--popup-anchor-bottom);
        max-height: var(--popup-anchor-max-height);
      }
    }
  }
}
</style>
